import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, PlaylistAdd, PlaylistRemove } from '@mui/icons-material'
import { Box, Button, Card, Chip, MenuItem, Stack, TableContainer, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { GameIcon } from '../../../../../features/game'
import { Game } from '../../../../../features/game/types/Game'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { Setting } from '../../../../../types/Setting'
import { useGetInternalGamesByIdsQuery } from '../../../../api/internal-games'
import { useGetInternalSettingQuery, useSaveInternalSettingMutation } from '../../../../api/settings'
import { DiscardChangesDialog } from '../../../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InternalGameSelectorDialog } from '../../../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { useEditPageLeave } from '../../../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { ComparedGame, FeaturedGamesSettingData } from '../../types/types'
import {
  buildFeaturedGamesSetting,
  featuredGamesFieldLabelKeys,
  findGameMarketIso,
  getUnstoredFeaturedGamesFields,
  maxComparedGames,
  parseCompareOrder,
  resolveComparedGames,
} from '../helpers/featuredGamesHelpers'

type FeaturedGameRow = { id: string; game?: Game; isCompared: boolean }
type ComparedGameRow = ComparedGame & { game?: Game; position: number }
type Draft = { featuredIds: string[]; compared: ComparedGame[] }

type FeaturedCustomProps = {
  canCompare: boolean
  onCompare: (row: FeaturedGameRow) => void
  onUncompare: (row: FeaturedGameRow) => void
  onRemove: (row: FeaturedGameRow) => void
}
type ComparedCustomProps = { onMarketChange: (gameId: string, marketIso: string) => void }

const emptyIds: string[] = []
const GameNameCell: FC<{ id: string; game?: Game }> = ({ id, game }) => {
  const { t } = useTranslation()
  if (!game) {
    return (
      <Box>
        <Typography color="text.secondary">{t('internal-service-management:settings_featured_unknown_game')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {id}
        </Typography>
      </Box>
    )
  }
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <GameIcon src={game.getIcon() as string} gameName={game.name ?? ''} size="small" />
      <Typography fontWeight="bold">{game.name}</Typography>
    </Stack>
  )
}

export const FeaturedGamesTab: FC = () => {
  const { t } = useTranslation()
  const { run, notify, notifySuccess, notifyError } = useMutationFeedback()
  const settingQuery = useGetInternalSettingQuery('featured_games')
  const [saveSetting, { isLoading: isSaving }] = useSaveInternalSettingMutation()
  const [draft, setDraft] = useState<Draft>()
  const [addedGames, setAddedGames] = useState<Record<string, Game>>({})
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<FeaturedGameRow>>()

  const storedSetting = settingQuery.data as Setting<FeaturedGamesSettingData> | null | undefined
  const storedIds = storedSetting?.data?.featuredGames ?? emptyIds
  const gamesQuery = useGetInternalGamesByIdsQuery(storedIds, { skip: !storedIds.length })

  const gamesById = useMemo(
    () => ({ ...Object.fromEntries((gamesQuery.data ?? []).map((game) => [game.id, game])), ...addedGames } as Record<string, Game | undefined>),
    [addedGames, gamesQuery.data]
  )
  const storedCompared = useMemo(
    () => resolveComparedGames(parseCompareOrder(storedSetting?.data?.compareOrder), storedIds, gamesById),
    [gamesById, storedIds, storedSetting?.data?.compareOrder]
  )

  const featuredIds = draft?.featuredIds ?? storedIds
  const compared = draft?.compared ?? storedCompared
  const sentSetting = useMemo(() => buildFeaturedGamesSetting(storedSetting, featuredIds, compared), [compared, featuredIds, storedSetting])
  const isDirty = getUnstoredFeaturedGamesFields(sentSetting, storedSetting).length > 0
  // The settings tabs are separate routes, so this also asks before another tab replaces this one.
  const { guard } = useEditPageLeave(isDirty)

  const isLoading = settingQuery.isLoading || (!!storedIds.length && gamesQuery.isLoading)
  const isError = settingQuery.isError || gamesQuery.isError

  const edit = useCallback(
    (update: (current: Draft) => Draft) => setDraft((previous) => update(previous ?? { featuredIds: storedIds, compared: storedCompared })),
    [storedCompared, storedIds]
  )

  const featuredRows: FeaturedGameRow[] = useMemo(
    () => featuredIds.map((id) => ({ id, game: gamesById[id], isCompared: compared.some((candidate) => candidate.gameId === id) })),
    [compared, featuredIds, gamesById]
  )
  const comparedRows: ComparedGameRow[] = useMemo(
    () => compared.map((candidate, index) => ({ ...candidate, game: gamesById[candidate.gameId], position: index + 1 })),
    [compared, gamesById]
  )

  const featuredColumns: GRTableColumn<FeaturedGameRow, FeaturedCustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:settings_featured_game'),
        accessor: ({ row }) => <GameNameCell id={row.id} game={row.game} />,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 260 } },
      },
      {
        labelAccessor: t('internal-service-management:settings_featured_publisher'),
        accessor: ({ row }) => row.game?.artist ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-service-management:settings_featured_comparison'),
        accessor: ({ row }) =>
          row.isCompared ? <Chip size="small" color="primary" label={t('internal-service-management:settings_featured_compared')} /> : null,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 140 } },
      },
      rowActionsColumn<FeaturedGameRow, FeaturedCustomProps>({
        actions: ({ row, customTableProps }) => {
          const canCompare = !!customTableProps?.canCompare && !!row.game?.markets?.length
          return [
            row.isCompared
              ? {
                  icon: <PlaylistRemove />,
                  label: t('internal-service-management:settings_featured_uncompare'),
                  onClick: () => customTableProps?.onUncompare(row),
                }
              : {
                  icon: <PlaylistAdd />,
                  label: t(
                    canCompare ? 'internal-service-management:settings_featured_compare' : 'internal-service-management:settings_featured_cannot_compare'
                  ),
                  ariaLabel: t('internal-service-management:settings_featured_compare'),
                  disabled: !canCompare,
                  onClick: () => customTableProps?.onCompare(row),
                },
            { type: 'delete', label: t('internal-service-management:settings_featured_remove'), onClick: () => customTableProps?.onRemove(row) },
          ]
        },
        width: 120,
      }),
    ],
    [t]
  )
  const [featuredTableColumns, setFeaturedTableColumns] = useState(featuredColumns)

  const comparedColumns: GRTableColumn<ComparedGameRow, ComparedCustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: '#',
        accessor: ({ row }) => row.position,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 60 } },
      },
      {
        labelAccessor: t('internal-service-management:settings_featured_game'),
        accessor: ({ row }) => <GameNameCell id={row.gameId} game={row.game} />,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 260 } },
      },
      {
        labelAccessor: t('internal-service-management:settings_featured_market'),
        accessor: ({ row, customTableProps }) => (
          <TextField
            select
            size="small"
            value={row.marketIso}
            onChange={(event) => customTableProps?.onMarketChange(row.gameId, event.target.value)}
            SelectProps={{
              SelectDisplayProps: { 'aria-label': t('internal-service-management:settings_featured_market_for', { name: row.game?.name ?? row.gameId }) },
            }}
            sx={{ minWidth: 220 }}
          >
            {(row.game?.markets ?? []).map((market) => (
              <MenuItem key={market.iso} value={market.iso}>
                {market.name}
              </MenuItem>
            ))}
          </TextField>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 260 } },
      },
    ],
    [t]
  )
  const [comparedTableColumns, setComparedTableColumns] = useState(comparedColumns)

  const featuredCustomProps = useMemo<FeaturedCustomProps>(
    () => ({
      canCompare: compared.length < maxComparedGames,
      onCompare: (row) =>
        edit((current) => {
          const marketIso = findGameMarketIso(row.game, '')
          return marketIso ? { ...current, compared: [...current.compared, { gameId: row.id, marketIso }] } : current
        }),
      onUncompare: (row) => edit((current) => ({ ...current, compared: current.compared.filter((candidate) => candidate.gameId !== row.id) })),
      onRemove: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:settings_featured_remove'),
          confirmText: t('internal-service-management:settings_featured_remove_confirm', { name: row.game?.name ?? row.id }),
          actionText: t('internal-service-management:settings_featured_remove'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [compared.length, edit, t]
  )
  const comparedCustomProps = useMemo<ComparedCustomProps>(
    () => ({
      onMarketChange: (gameId, marketIso) =>
        edit((current) => ({
          ...current,
          compared: current.compared.map((candidate) => (candidate.gameId === gameId ? { ...candidate, marketIso } : candidate)),
        })),
    }),
    [edit]
  )

  const handleConfirmRemove = (confirmed?: ConfirmDialogData<FeaturedGameRow>) => {
    setConfirmDialogData(undefined)
    const row = confirmed?.data
    if (!row) return
    edit((current) => ({
      featuredIds: current.featuredIds.filter((id) => id !== row.id),
      compared: current.compared.filter((candidate) => candidate.gameId !== row.id),
    }))
  }

  const handleGameSelected = (games: Game[]) => {
    setIsSelectorOpen(false)
    const game = games[0]
    if (!game) return
    if (featuredIds.includes(game.id)) {
      notify(t('internal-service-management:settings_featured_already', { name: game.name }), 'info')
      return
    }
    setAddedGames((previous) => ({ ...previous, [game.id]: game }))
    edit((current) => ({ ...current, featuredIds: [...current.featuredIds, game.id] }))
  }

  const handleSave = async () => {
    const saved = await run(saveSetting(sentSetting).unwrap(), { error: t('internal-service-management:settings_featured_save_failed') })
    if (!saved.ok) return
    const unstored = getUnstoredFeaturedGamesFields(sentSetting, saved.result as Setting<FeaturedGamesSettingData> | null)
    if (unstored.length) {
      const fields = unstored.map((field) => t(featuredGamesFieldLabelKeys[field])).join(', ')
      notifyError(t('internal-service-management:settings_featured_not_stored', { fields }))
      return
    }
    setDraft(undefined)
    notifySuccess(t('internal-service-management:settings_featured_saved'))
  }

  return (
    <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:settings_load_failed')}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography color="text.secondary" flexGrow={1}>
            {t('internal-service-management:settings_featured_description')}
          </Typography>
          {isDirty && <Chip color="warning" variant="outlined" label={t('internal-service-management:settings_unsaved_changes')} />}
          <Button onClick={() => setDraft(undefined)} disabled={!draft || isSaving}>
            {t('internal-service-management:settings_reset_changes')}
          </Button>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setIsSelectorOpen(true)}>
            {t('internal-service-management:settings_featured_add')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!isDirty || isSaving}>
            {t('internal-service-management:settings_save')}
          </Button>
        </Stack>

        <TableContainer component={Card}>
          <GRTable
            rows={featuredRows}
            columns={featuredTableColumns}
            onColumnsUpdated={setFeaturedTableColumns}
            rowIdKey={(row) => row.id}
            customProps={featuredCustomProps}
            noRowsLabel={t('internal-service-management:settings_featured_none')}
            striped
            hoverable
          />
        </TableContainer>

        <Box>
          <Typography variant="h6" fontWeight="bold">
            {t('internal-service-management:settings_featured_comparison_title')}
          </Typography>
          <Typography color="text.secondary">{t('internal-service-management:settings_featured_comparison_description', { max: maxComparedGames })}</Typography>
        </Box>
        <TableContainer component={Card}>
          <GRTable
            rows={comparedRows}
            columns={comparedTableColumns}
            onColumnsUpdated={setComparedTableColumns}
            rowIdKey={(row) => row.gameId}
            customProps={comparedCustomProps}
            onSortRows={(sortedRows) => edit((current) => ({ ...current, compared: sortedRows.map(({ gameId, marketIso }) => ({ gameId, marketIso })) }))}
            noRowsLabel={t('internal-service-management:settings_featured_comparison_none')}
            striped
          />
        </TableContainer>

        <InternalGameSelectorDialog
          open={isSelectorOpen}
          title={t('internal-service-management:settings_featured_add')}
          maxSelected={1}
          onClose={() => setIsSelectorOpen(false)}
          onDone={handleGameSelected}
        />
        <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmRemove} />
        <DiscardChangesDialog
          guard={guard}
          title={t('internal-service-management:settings_discard_title')}
          text={t('internal-service-management:settings_discard_confirm')}
          discardText={t('internal-service-management:settings_discard')}
        />
      </Stack>
    </QueryStateBoundary>
  )
}
